import React from 'react';
import { Card, CardBody, Pagination, Chip, Button, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { useLocale } from '~/hooks/useLocale';
import { ReviewListFilterValues, ReviewSortBy, SortOrder } from '~/types/reviews';
import { FiFilter, FiChevronDown } from 'react-icons/fi';
import { DateFormatter } from '@internationalized/date';
import { ReviewListItem } from "../ReviewListItem";
import { ReviewResponseDto } from '@bitecritic-api/src/reviews/dto/review.response.dto';

interface ReviewListProps {
  reviews: ReviewResponseDto[];
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  filters?: ReviewListFilterValues;
  onFilterClick: () => void;
  onSortChange: (sortBy: ReviewSortBy, sortOrder: SortOrder) => void;
  isLoading?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => Promise<void>;
  showPlaceCard?: boolean;
  showUserCard?: boolean;
  showFoodCard?: boolean;
}

export const ReviewList: React.FC<ReviewListProps> = ({
  reviews,
  total,
  page,
  limit,
  onPageChange,
  filters,
  onFilterClick,
  onSortChange,
  isLoading,
  hasNextPage,
  onLoadMore,
  showPlaceCard = true,
  showUserCard = true,
  showFoodCard = true,
}) => {
  const { t } = useTranslation();
  const totalPages = Math.ceil(total / limit);
  const { currentLocale } = useLocale();

  const renderActiveFilters = () => {
    if (!filters) return null;

    const activeFilters = [];

    if (filters.minRating) {
      activeFilters.push(
        <Chip key="rating" className="mr-2">
          {t('reviews.filters.active.rating', { value: filters.minRating })}
        </Chip>
      );
    }

    if (filters.startDate) {
      activeFilters.push(
        <Chip key="startDate" className="mr-2">
          {t('reviews.filters.active.startDate', {
            date: new DateFormatter(currentLocale.code).format(new Date(filters.startDate))
          })}
        </Chip>
      );
    }

    if (filters.endDate) {
      activeFilters.push(
        <Chip key="endDate" className="mr-2">
          {t('reviews.filters.active.endDate', {
            date: new DateFormatter(currentLocale.code).format(new Date(filters.endDate))
          })}
        </Chip>
      );
    }

    if (filters.minCost) {
      activeFilters.push(
        <Chip key="cost" className="mr-2">
          {t('reviews.filters.active.cost', { value: filters.minCost })}
        </Chip>
      );
    }

    if (filters.radiusInKm) {
      activeFilters.push(
        <Chip key="radius" className="mr-2">
          {t('reviews.filters.active.radius', { value: filters.radiusInKm })}
        </Chip>
      );
    }

    return activeFilters.length > 0 ? (
      <div className="flex flex-wrap gap-2 mb-4">
        {activeFilters}
      </div>
    ) : null;
  };

  const getSortLabel = (sortBy?: ReviewSortBy, sortOrder?: SortOrder) => {
    if (!sortBy || !sortOrder) return '';
    const direction = t(`reviews.filters.sortOrder.${sortOrder}`);
    const field = t(`reviews.filters.sortBy.${sortBy}`);
    return `${field} (${direction})`;
  };

  return (
    <div className="space-y-4 pt-4">
      {/* Header with Title, Count, Sort and Filter Button */}
      <div className="flex justify-between">
        <div className="flex gap-2">
          <h2 className="text-xl font-semibold">{t('common.reviews')}</h2>
          <span className="text-default-500">({total})</span>
        </div>
        <div className="flex flex-col justify-end gap-2">
          <Dropdown>
            <DropdownTrigger>
              <Button
                variant="flat"
                size="sm"
                endContent={<FiChevronDown />}
              >
                {getSortLabel(filters?.sortBy, filters?.sortOrder)}
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Sort options"
              onAction={(key) => {
                const [sortBy, sortOrder] = String(key).split('-');
                onSortChange(sortBy as ReviewSortBy, sortOrder as SortOrder);
              }}
            >
              <DropdownItem key={`${ReviewSortBy.CREATED_AT}-${SortOrder.DESC}`}>
                {t('reviews.filters.sort.newest')}
              </DropdownItem>
              <DropdownItem key={`${ReviewSortBy.CREATED_AT}-${SortOrder.ASC}`}>
                {t('reviews.filters.sort.oldest')}
              </DropdownItem>
              <DropdownItem key={`${ReviewSortBy.LIKE_COUNT}-${SortOrder.DESC}`}>
                {t('reviews.filters.sort.mostLiked')}
              </DropdownItem>
              <DropdownItem key={`${ReviewSortBy.LIKE_COUNT}-${SortOrder.ASC}`}>
                {t('reviews.filters.sort.leastLiked')}
              </DropdownItem>
              <DropdownItem key={`${ReviewSortBy.COST}-${SortOrder.DESC}`}>
                {t('reviews.filters.sort.mostExpensive')}
              </DropdownItem>
              <DropdownItem key={`${ReviewSortBy.COST}-${SortOrder.ASC}`}>
                {t('reviews.filters.sort.cheapest')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
          <Button
            color="primary"
            variant="flat"
            onPress={onFilterClick}
            startContent={<FiFilter />}
            size="sm"
          >
            {t('reviews.filters.title')}
          </Button>
        </div>
      </div>

      {/* Active Filters */}
      {renderActiveFilters()}

      {/* Reviews */}
      {reviews.length === 0 ? (
        <Card>
          <CardBody className="py-8">
            <div className="text-center text-default-500">
              <p>{t('reviews.noReviews')}</p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="flex flex-col gap-4">
            {reviews.map((review) => (
              <ReviewListItem
                key={review.id}
                review={review}
                showPlaceCard={showPlaceCard}
                showUserCard={showUserCard}
                showFoodCard={showFoodCard}
              />
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-center mt-8">
              <Button
                color="primary"
                variant="flat"
                onPress={onLoadMore}
                isLoading={isLoading}
              >
                {t('common.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && !hasNextPage && (
        <div className="flex justify-center mt-8">
          <Pagination
            total={totalPages}
            page={page}
            onChange={onPageChange}
            showControls
          />
        </div>
      )}
      
    </div>
  );
};